(() => {
  'use strict';

  const SIZE_VALUES = new Set([
    'xxs',
    'xs',
    's',
    'm',
    'l',
    'xl',
    '2xl',
    '3xl',
    '4xl',
    '5xl',
    '6xl'
  ]);

  const SIZE_TYPE_VALUES = new Set([
    'regular',
    'petite',
    'tall'
  ]);

  const EXCLUDED_AREAS = [
    '.pl-swatches',
    '.complete-the-set',
    '.complete-set',
    '[class*="complete-the-set"]',
    '[id*="complete-the-set"]',
    '.related-products',
    '.product-recommendations',
    '.product-card',
    '.grid-product',
    '.quick-product',
    '.quick-add',
    '[data-product-card]',
    '[data-product-recommendations]'
  ].join(',');

  const MESSAGE_TEXT =
    'Please select a size to add to the bag.';

  const REQUIRED_BUTTON_TEXT =
    'SELECT A SIZE';

  const OPTION_WRAPPER_SELECTOR = [
    'li',
    '.variant-input',
    '.variant-input-wrap',
    '.option-value',
    '.avis-option-value-item',
    '.product-option-value'
  ].join(',');

  const DYNAMIC_PAYMENT_SELECTOR = [
    '.shopify-payment-button',
    '.shopify-payment-button__button',
    '.shopify-payment-button__more-options'
  ].join(',');

  let sizeInputs = [];
  let sizeTypeInputs = [];

  let activeProduct = null;

  let userInteracted = false;
  let selectedSize = false;
  let selectedSizeType = false;

  let clearing = false;
  let initialObserver = null;
  let initialFrame = 0;
  let initialTimer = null;
  let productSwitchToken = 0;

  function normalize(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function productScope() {
    return (
      document.querySelector(
        '[id^="ProductSection-"]'
      ) ||
      document.querySelector(
        '[id*="ProductSection"]'
      ) ||
      document.querySelector(
        '[data-section-type="product"]'
      ) ||
      document.querySelector(
        '.product-section'
      ) ||
      document.querySelector(
        '#MainContent'
      ) ||
      document.body
    );
  }

  function excluded(element) {
    return Boolean(
      element?.closest?.(
        EXCLUDED_AREAS
      )
    );
  }

  function getLabel(input) {
    if (
      !(
        input instanceof
        HTMLInputElement
      )
    ) {
      return null;
    }

    if (
      input.nextElementSibling instanceof
        HTMLLabelElement
    ) {
      return input.nextElementSibling;
    }

    if (input.id) {
      const label =
        document.querySelector(
          `label[for="${CSS.escape(
            input.id
          )}"]`
        );

      if (label) {
        return label;
      }
    }

    return input.closest(
      'label'
    );
  }

  function getWrapper(input) {
    const label =
      getLabel(input);

    return (
      label?.closest(
        OPTION_WRAPPER_SELECTOR
      ) ||
      label ||
      input?.parentElement ||
      null
    );
  }

  function collectGroups() {
    const groups =
      new Map();

    productScope()
      .querySelectorAll(
        'input[type="radio"][name]'
      )
      .forEach((input) => {
        if (
          !(
            input instanceof
              HTMLInputElement
          ) ||
          excluded(input)
        ) {
          return;
        }

        if (
          !groups.has(
            input.name
          )
        ) {
          groups.set(
            input.name,
            []
          );
        }

        groups
          .get(input.name)
          .push(input);
      });

    sizeInputs = [];
    sizeTypeInputs = [];

    for (
      const group of
      groups.values()
    ) {
      const values =
        group.map((input) =>
          normalize(input.value)
        );

      if (
        values.some((value) =>
          SIZE_TYPE_VALUES.has(
            value
          )
        )
      ) {
        sizeTypeInputs =
          group;

        continue;
      }

      if (
        values.some((value) =>
          SIZE_VALUES.has(
            value
          )
        )
      ) {
        sizeInputs =
          group;
      }
    }

    return (
      sizeInputs.length > 0
    );
  }

  function groupType(input) {
    if (
      sizeInputs.includes(input)
    ) {
      return 'size';
    }

    if (
      sizeTypeInputs.includes(
        input
      )
    ) {
      return 'size-type';
    }

    return '';
  }

  function inputFromTarget(
    target
  ) {
    if (
      !(target instanceof Element)
    ) {
      return null;
    }

    if (
      target instanceof
        HTMLInputElement &&
      target.type === 'radio'
    ) {
      return target;
    }

    const label =
      target.closest('label');

    if (!label) {
      return null;
    }

    if (label.htmlFor) {
      const linked =
        document.getElementById(
          label.htmlFor
        );

      if (
        linked instanceof
          HTMLInputElement &&
        linked.type === 'radio'
      ) {
        return linked;
      }
    }

    const nested =
      label.querySelector(
        'input[type="radio"]'
      );

    if (
      nested instanceof
        HTMLInputElement
    ) {
      return nested;
    }

    const previous =
      label.previousElementSibling;

    return (
      previous instanceof
        HTMLInputElement &&
      previous.type === 'radio'
    )
      ? previous
      : null;
  }

  function removeSelectionClasses(
    element
  ) {
    if (
      !(element instanceof Element)
    ) {
      return;
    }

    [
      'selected',
      'is-selected',
      'active',
      'is-active',
      'checked',
      'is-checked',
      'current',
      'avis-selected',
      'comenii-size-selected',
      'comenii-size-unselected'
    ].forEach((className) => {
      element.classList.remove(
        className
      );
    });

    if (
      element.hasAttribute(
        'aria-checked'
      )
    ) {
      element.setAttribute(
        'aria-checked',
        'false'
      );
    }

    if (
      element.hasAttribute(
        'aria-pressed'
      )
    ) {
      element.setAttribute(
        'aria-pressed',
        'false'
      );
    }
  }

  function isUnavailable(input) {
    if (
      !(
        input instanceof
        HTMLInputElement
      )
    ) {
      return false;
    }

    const label =
      getLabel(input);

    const wrapper =
      getWrapper(input);

    const classText = [
      label?.className || '',
      wrapper?.className || ''
    ].join(' ');

    return Boolean(
      input.disabled ||
      input.getAttribute(
        'aria-disabled'
      ) === 'true' ||
      input.dataset
        .comeniiAvailable ===
        'false' ||
      label?.getAttribute(
        'aria-disabled'
      ) === 'true' ||
      wrapper?.getAttribute(
        'aria-disabled'
      ) === 'true' ||
      /\b(disabled|unavailable|sold[-_ ]?out|is-disabled)\b/i.test(
        classText
      )
    );
  }

  function applyVisualState(
    inputs
  ) {
    inputs.forEach((input) => {
      const label =
        getLabel(input);

      const wrapper =
        getWrapper(input);

      if (!label) {
        return;
      }

      removeSelectionClasses(
        label
      );

      if (
        wrapper &&
        wrapper !== label
      ) {
        removeSelectionClasses(
          wrapper
        );
      }

      if (
        isUnavailable(input)
      ) {
        label.classList.add(
          'comenii-size-unavailable'
        );

        return;
      }

      label.classList.remove(
        'comenii-size-unavailable'
      );

      if (input.checked) {
        label.classList.add(
          'comenii-size-selected'
        );

        label.setAttribute(
          'aria-checked',
          'true'
        );
      } else {
        label.classList.add(
          'comenii-size-unselected'
        );
      }
    });
  }

  function optionName(option) {
    return (
      typeof option === 'string'
        ? option
        : option?.name || ''
    );
  }

  function variantOptionValue(
    variant,
    index
  ) {
    return normalize(
      variant?.options?.[
        index
      ] ??
      variant?.[
        `option${index + 1}`
      ] ??
      ''
    );
  }

  function productOptionValues(
    product,
    index
  ) {
    return Array.from(
      new Set(
        (
          product?.variants ||
          []
        )
          .map((variant) => {
            return variantOptionValue(
              variant,
              index
            );
          })
          .filter(Boolean)
      )
    );
  }

  function productOptionIndexes(
    product
  ) {
    const optionCount =
      Math.max(
        product?.options?.length ||
          0,

        product?.variants?.[0]
          ?.options?.length ||
          0,

        3
      );

    let sizeIndex = -1;
    let sizeTypeIndex = -1;

    for (
      let index = 0;
      index < optionCount;
      index += 1
    ) {
      const name =
        normalize(
          optionName(
            product?.options?.[
              index
            ]
          )
        );

      const values =
        productOptionValues(
          product,
          index
        );

      const containsSizeType =
        values.some((value) => {
          return SIZE_TYPE_VALUES.has(
            value
          );
        });

      const containsSize =
        values.some((value) => {
          return SIZE_VALUES.has(
            value
          );
        });

      if (
        sizeTypeIndex < 0 &&
        (
          /size[\s_-]*type/.test(
            name
          ) ||
          containsSizeType
        )
      ) {
        sizeTypeIndex =
          index;

        continue;
      }

      if (
        sizeIndex < 0 &&
        (
          (
            name.includes(
              'size'
            ) &&
            !name.includes(
              'type'
            )
          ) ||
          containsSize
        )
      ) {
        sizeIndex =
          index;
      }
    }

    return {
      sizeIndex,
      sizeTypeIndex
    };
  }

  function checkedValue(inputs) {
    const selected =
      inputs.find((input) => {
        return (
          input.checked &&
          !input.disabled
        );
      });

    return selected
      ? normalize(
          selected.value
        )
      : '';
  }

  function inputAvailableForProduct(
    product,
    input,
    type,
    indexes
  ) {
    const variants =
      Array.isArray(
        product?.variants
      )
        ? product.variants
        : [];

    if (!variants.length) {
      return true;
    }

    const optionIndex =
      type === 'size'
        ? indexes.sizeIndex
        : indexes.sizeTypeIndex;

    if (optionIndex < 0) {
      return true;
    }

    const inputValue =
      normalize(
        input.value
      );

    const currentSize =
      checkedValue(
        sizeInputs
      );

    const currentSizeType =
      checkedValue(
        sizeTypeInputs
      );

    return variants.some(
      (variant) => {
        if (!variant.available) {
          return false;
        }

        if (
          variantOptionValue(
            variant,
            optionIndex
          ) !== inputValue
        ) {
          return false;
        }

        if (
          type === 'size' &&
          indexes.sizeTypeIndex >= 0 &&
          currentSizeType &&
          variantOptionValue(
            variant,
            indexes.sizeTypeIndex
          ) !== currentSizeType
        ) {
          return false;
        }

        if (
          type === 'size-type' &&
          indexes.sizeIndex >= 0 &&
          currentSize &&
          variantOptionValue(
            variant,
            indexes.sizeIndex
          ) !== currentSize
        ) {
          return false;
        }

        return true;
      }
    );
  }

  function setInputAvailability(
    input,
    available
  ) {
    if (
      !(
        input instanceof
        HTMLInputElement
      )
    ) {
      return;
    }

    const label =
      getLabel(input);

    const wrapper =
      getWrapper(input);

    input.disabled =
      !available;

    input.setAttribute(
      'aria-disabled',
      available
        ? 'false'
        : 'true'
    );

    input.dataset
      .comeniiAvailable =
      available
        ? 'true'
        : 'false';

    if (!available) {
      input.checked =
        false;

      input.defaultChecked =
        false;

      input.removeAttribute(
        'checked'
      );

      input.setAttribute(
        'aria-checked',
        'false'
      );
    }

    [
      label,
      wrapper
    ].forEach((element) => {
      if (
        !(element instanceof Element)
      ) {
        return;
      }

      [
        'disabled',
        'is-disabled',
        'unavailable',
        'sold-out',
        'sold_out'
      ].forEach((className) => {
        element.classList.remove(
          className
        );
      });

      element.classList.toggle(
        'comenii-size-unavailable',
        !available
      );

      element.classList.toggle(
        'comenii-size-available',
        available
      );

      element.setAttribute(
        'aria-disabled',
        available
          ? 'false'
          : 'true'
      );

      element.dataset
        .comeniiAvailable =
        available
          ? 'true'
          : 'false';
    });
  }

  function applyProductAvailability(
    product
  ) {
    if (
      !product ||
      !collectGroups()
    ) {
      return;
    }

    activeProduct =
      product;

    const indexes =
      productOptionIndexes(
        product
      );

    sizeInputs.forEach(
      (input) => {
        setInputAvailability(
          input,
          inputAvailableForProduct(
            product,
            input,
            'size',
            indexes
          )
        );
      }
    );

    sizeTypeInputs.forEach(
      (input) => {
        setInputAvailability(
          input,
          inputAvailableForProduct(
            product,
            input,
            'size-type',
            indexes
          )
        );
      }
    );

    selectedSize =
      sizeInputs.some((input) => {
        return (
          input.checked &&
          !input.disabled
        );
      });

    selectedSizeType =
      sizeTypeInputs.some(
        (input) => {
          return (
            input.checked &&
            !input.disabled
          );
        }
      );

    applyVisualState(
      sizeInputs
    );

    applyVisualState(
      sizeTypeInputs
    );
  }

  function findHeadingNode(type) {
    const walker =
      document.createTreeWalker(
        productScope(),
        NodeFilter.SHOW_TEXT
      );

    let node;

    while (
      (
        node =
          walker.nextNode()
      )
    ) {
      const text =
        String(
          node.nodeValue || ''
        )
          .replace(/\s+/g, ' ')
          .trim();

      if (
        type === 'size-type' &&
        /^size\s*type(?:\s*:.*)?$/i.test(
          text
        )
      ) {
        return node;
      }

      if (
        type === 'size' &&
        /^size(?!\s*type)(?:\s*:.*)?$/i.test(
          text
        )
      ) {
        return node;
      }
    }

    return null;
  }

  function setHeading(
    type,
    value
  ) {
    const node =
      findHeadingNode(type);

    if (!node) {
      return;
    }

    node.nodeValue =
      type === 'size-type'
        ? `SIZE TYPE: ${value}`
        : `Size: ${value}`;
  }

  function sizeContainer() {
    const first =
      sizeInputs[0];

    if (!first) {
      return null;
    }

    return (
      first.closest(
        'ul.avis-option-values'
      ) ||
      first.closest(
        '.avis-option-values'
      ) ||
      first.closest(
        '.avis-swatch-variants'
      ) ||
      first.closest(
        'fieldset'
      ) ||
      first.closest(
        '.variant-input-wrap'
      ) ||
      first.closest(
        '.variant-wrapper'
      ) ||
      getLabel(first)
        ?.parentElement ||
      first.parentElement
    );
  }

  function ensureMessage() {
    if (selectedSize) {
      return;
    }

    const container =
      sizeContainer();

    if (!container) {
      return;
    }

    let message =
      productScope()
        .querySelector(
          '.comenii-select-size-message'
        );

    if (!message) {
      message =
        document.createElement(
          'div'
        );

      message.className =
        'comenii-select-size-message';

      container.insertAdjacentElement(
        'afterend',
        message
      );
    }

    message.textContent =
      MESSAGE_TEXT;

    message.hidden =
      false;
  }

  function hideMessage() {
    const message =
      productScope()
        .querySelector(
          '.comenii-select-size-message'
        );

    if (message) {
      message.hidden =
        true;
    }
  }

  function mainForm() {
    const forms =
      productScope()
        .querySelectorAll(
          'form[action*="/cart/add"]'
        );

    for (const form of forms) {
      if (
        !(
          form instanceof
            HTMLFormElement
        ) ||
        excluded(form)
      ) {
        continue;
      }

      if (
        form.querySelector(
          [
            'button[name="add"]',
            'button[type="submit"]',
            '.add-to-cart',
            '.product-form__submit'
          ].join(',')
        )
      ) {
        return form;
      }
    }

    return null;
  }

  function buyButton() {
    const form =
      mainForm();

    if (!form) {
      return null;
    }

    const buttons =
      form.querySelectorAll(
        [
          'button[name="add"]',
          'button[type="submit"]',
          '.add-to-cart',
          '.product-form__submit'
        ].join(',')
      );

    for (const button of buttons) {
      if (
        !(
          button instanceof
            HTMLElement
        )
      ) {
        continue;
      }

      if (
        button.closest(
          '.shopify-payment-button'
        )
      ) {
        continue;
      }

      const style =
        getComputedStyle(
          button
        );

      if (
        style.display !== 'none' &&
        style.visibility !== 'hidden'
      ) {
        return button;
      }
    }

    return null;
  }

  function dynamicPaymentElements() {
    const form =
      mainForm();

    if (!form) {
      return [];
    }

    return Array.from(
      form.querySelectorAll(
        DYNAMIC_PAYMENT_SELECTOR
      )
    );
  }

  function setDynamicPaymentLocked(
    locked
  ) {
    dynamicPaymentElements()
      .forEach((element) => {
        if (
          !(element instanceof Element)
        ) {
          return;
        }

        element.classList.toggle(
          'comenii-payment-locked',
          locked
        );

        element.setAttribute(
          'aria-disabled',
          locked
            ? 'true'
            : 'false'
        );
      });
  }

  function setButtonText(
    button,
    text
  ) {
    if (!button) {
      return;
    }

    const walker =
      document.createTreeWalker(
        button,
        NodeFilter.SHOW_TEXT
      );

    let node;
    let fallback = null;

    while (
      (
        node =
          walker.nextNode()
      )
    ) {
      if (
        !node.nodeValue.trim()
      ) {
        continue;
      }

      fallback ||= node;

      if (
        /sold out|add to cart|add to bag|select a size|unavailable/i.test(
          node.nodeValue
        )
      ) {
        node.nodeValue =
          text;

        return;
      }
    }

    if (fallback) {
      fallback.nodeValue =
        text;
    } else {
      button.textContent =
        text;
    }
  }

  function allRequiredSelected() {
    const sizeOkay =
      sizeInputs.length === 0 ||
      selectedSize;

    const typeOkay =
      sizeTypeInputs.length === 0 ||
      selectedSizeType;

    return Boolean(
      sizeOkay &&
      typeOkay
    );
  }

  function applyRequiredButton() {
    if (
      allRequiredSelected()
    ) {
      releaseRequiredButton();
      return;
    }

    const button =
      buyButton();

    const form =
      mainForm();

    if (!form) {
      return;
    }

    form.classList.add(
      'comenii-size-required'
    );

    setDynamicPaymentLocked(
      true
    );

    if (!button) {
      return;
    }

    if (
      !button.dataset
        .comeniiOriginalText &&
      !/select a size/i.test(
        button.textContent || ''
      )
    ) {
      button.dataset
        .comeniiOriginalText =
        String(
          button.textContent || ''
        ).trim();
    }

    button.disabled =
      false;

    button.removeAttribute(
      'disabled'
    );

    button.setAttribute(
      'aria-disabled',
      'true'
    );

    button.dataset
      .comeniiSizeRequired =
      'true';

    setButtonText(
      button,
      REQUIRED_BUTTON_TEXT
    );
  }

  function releaseRequiredButton() {
    const button =
      buyButton();

    const form =
      mainForm();

    if (!form) {
      return;
    }

    form.classList.remove(
      'comenii-size-required'
    );

    setDynamicPaymentLocked(
      false
    );

    if (!button) {
      return;
    }

    delete button.dataset
      .comeniiSizeRequired;

    button.removeAttribute(
      'aria-disabled'
    );

    [
      30,
      120,
      300,
      600
    ].forEach((delay) => {
      window.setTimeout(
        () => {
          const latest =
            buyButton();

          if (!latest) {
            return;
          }

          if (
            /select a size/i.test(
              latest.textContent ||
              ''
            )
          ) {
            const original =
              latest.dataset
                .comeniiOriginalText;

            setButtonText(
              latest,
              latest.disabled
                ? 'SOLD OUT'
                : (
                    original ||
                    'ADD TO CART'
                  )
            );
          }
        },
        delay
      );
    });
  }

  function updateDocumentState() {
    const selectedSizeInput =
      sizeInputs.find((input) => {
        return (
          input.checked &&
          !input.disabled
        );
      });

    const selectedTypeInput =
      sizeTypeInputs.find(
        (input) => {
          return (
            input.checked &&
            !input.disabled
          );
        }
      );

    document.documentElement
      .dataset
      .comeniiSizeSelected =
      allRequiredSelected()
        ? 'true'
        : 'false';

    if (selectedSizeInput) {
      document.documentElement
        .dataset
        .comeniiSelectedSize =
        normalize(
          selectedSizeInput.value
        );
    } else {
      delete document
        .documentElement
        .dataset
        .comeniiSelectedSize;
    }

    document.dispatchEvent(
      new CustomEvent(
        'comenii:size-selected',
        {
          detail: {
            size:
              selectedSizeInput
                ?.value || '',

            sizeType:
              selectedTypeInput
                ?.value || '',

            complete:
              allRequiredSelected()
          }
        }
      )
    );
  }

  function clearSelections() {
    collectGroups();

    [
      ...sizeInputs,
      ...sizeTypeInputs
    ].forEach((input) => {
      input.checked =
        false;

      input.defaultChecked =
        false;

      input.removeAttribute(
        'checked'
      );

      input.setAttribute(
        'aria-checked',
        'false'
      );

      const label =
        getLabel(input);

      const wrapper =
        getWrapper(input);

      [
        label,
        wrapper
      ].forEach((element) => {
        if (
          !(element instanceof Element)
        ) {
          return;
        }

        removeSelectionClasses(
          element
        );
      });
    });

    selectedSize =
      false;

    selectedSizeType =
      false;

    applyVisualState(
      sizeInputs
    );

    applyVisualState(
      sizeTypeInputs
    );

    setHeading(
      'size',
      'Select Size'
    );

    if (
      sizeTypeInputs.length
    ) {
      setHeading(
        'size-type',
        'Select Type'
      );
    }

    updateDocumentState();
    ensureMessage();
    applyRequiredButton();
  }

  function clearInitialSelection() {
    if (
      userInteracted ||
      clearing ||
      !collectGroups()
    ) {
      return;
    }

    clearing =
      true;

    clearSelections();

    clearing =
      false;
  }

  function resetForProductSwitch(
    product
  ) {
    if (!product) {
      return;
    }

    const token =
      ++productSwitchToken;

    activeProduct =
      product;

    userInteracted =
      false;

    clearing =
      true;

    clearSelections();

    applyProductAvailability(
      product
    );

    clearSelections();

    applyProductAvailability(
      product
    );

    updateDocumentState();
    ensureMessage();
    applyRequiredButton();

    clearing =
      false;

    [
      0,
      60,
      180,
      450,
      900,
      1500,
      2500
    ].forEach((delay) => {
      window.setTimeout(() => {
        if (
          token !==
          productSwitchToken
        ) {
          return;
        }

        applyProductAvailability(
          product
        );

        if (!userInteracted) {
          clearSelections();

          applyProductAvailability(
            product
          );

          ensureMessage();
          applyRequiredButton();
        }
      }, delay);
    });
  }

  function syncSelectedState(
    input
  ) {
    if (
      !(
        input instanceof
        HTMLInputElement
      )
    ) {
      return;
    }

    collectGroups();

    const type =
      groupType(input);

    if (
      !type ||
      input.disabled
    ) {
      return;
    }

    if (type === 'size') {
      selectedSize =
        sizeInputs.some(
          (radio) => {
            return (
              radio.checked &&
              !radio.disabled
            );
          }
        );

      const selected =
        sizeInputs.find(
          (radio) => {
            return (
              radio.checked &&
              !radio.disabled
            );
          }
        );

      if (selected) {
        setHeading(
          'size',
          selected.value
        );

        hideMessage();
      }
    }

    if (
      type === 'size-type'
    ) {
      selectedSizeType =
        sizeTypeInputs.some(
          (radio) => {
            return (
              radio.checked &&
              !radio.disabled
            );
          }
        );

      const selected =
        sizeTypeInputs.find(
          (radio) => {
            return (
              radio.checked &&
              !radio.disabled
            );
          }
        );

      if (selected) {
        setHeading(
          'size-type',
          selected.value
        );
      }
    }

    if (activeProduct) {
      applyProductAvailability(
        activeProduct
      );
    } else {
      applyVisualState(
        sizeInputs
      );

      applyVisualState(
        sizeTypeInputs
      );
    }

    updateDocumentState();

    if (
      allRequiredSelected()
    ) {
      releaseRequiredButton();
    } else {
      ensureMessage();
      applyRequiredButton();
    }
  }

  function scheduleSync(input) {
    [
      0,
      80,
      250
    ].forEach((delay) => {
      window.setTimeout(
        () => {
          syncSelectedState(
            input
          );
        },
        delay
      );
    });
  }

  function installStyles() {
    document
      .getElementById(
        'comenii-size-selection-style'
      )
      ?.remove();

    const style =
      document.createElement(
        'style'
      );

    style.id =
      'comenii-size-selection-style';

    style.textContent = `
      .comenii-select-size-message {
  display: block !important;
  width: 100% !important;
  margin: 8px 0 12px !important;
  color: #b42346 !important;
  font-size: 13px !important;
  line-height: 1.4 !important;
  font-weight: 600 !important;
  text-align: left !important;
}

.comenii-select-size-message[hidden] {
  display: none !important;
}

      label.comenii-size-unselected {
        background: #ffffff !important;
        background-color: #ffffff !important;
        color: #111111 !important;
        border-color: #d9d9d9 !important;
        box-shadow: none !important;
        outline: none !important;
        opacity: 1 !important;
        cursor: pointer !important;
      }

      label.comenii-size-selected {
        background: #ffffff !important;
        background-color: #ffffff !important;
        color: #111111 !important;
        border: 2px solid #000000 !important;
        border-color: #000000 !important;
        box-shadow: none !important;
        outline: none !important;
        opacity: 1 !important;
        cursor: pointer !important;
      }

      label.comenii-size-unselected *,
      label.comenii-size-selected * {
        color: #111111 !important;
        background: transparent !important;
        background-color: transparent !important;
      }

      label.comenii-size-unavailable,
      .comenii-size-unavailable > label {
        position: relative !important;
        overflow: hidden !important;
        background-color: #ffffff !important;
        background-image:
          linear-gradient(
            to bottom right,
            transparent calc(50% - 0.5px),
            #d8d8d8 50%,
            transparent calc(50% + 0.5px)
          ) !important;
        color: #cfcfcf !important;
        border-color: #e1e1e1 !important;
        box-shadow: none !important;
        opacity: 1 !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
      }

      label.comenii-size-unavailable *,
      .comenii-size-unavailable > label * {
        color: #cfcfcf !important;
        background: transparent !important;
        background-color: transparent !important;
      }

      form.comenii-size-required
      .shopify-payment-button {
        opacity: 0.55 !important;
        pointer-events: none !important;
      }

      form.comenii-size-required
      .shopify-payment-button__button,
      form.comenii-size-required
      .shopify-payment-button__more-options {
        pointer-events: none !important;
        cursor: not-allowed !important;
      }
    `;

    document.head.appendChild(
      style
    );
  }

  function installListeners() {
    document.addEventListener(
      'pointerdown',
      (event) => {
        const input =
          inputFromTarget(
            event.target
          );

        if (!input) {
          return;
        }

        collectGroups();

        if (
          !groupType(input) ||
          input.disabled ||
          excluded(input)
        ) {
          return;
        }

        userInteracted =
          true;

        scheduleSync(
          input
        );
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        const input =
          event.target;

        if (
          !(
            input instanceof
              HTMLInputElement
          ) ||
          input.type !== 'radio'
        ) {
          return;
        }

        collectGroups();

        if (!groupType(input)) {
          return;
        }

        userInteracted =
          true;

        syncSelectedState(
          input
        );
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (
          allRequiredSelected() ||
          !(
            event.target instanceof
              Element
          )
        ) {
          return;
        }

        const target =
          event.target;

        const button =
          buyButton();

        const addToCartClicked =
          Boolean(
            button &&
            target.closest(
              'button,[role="button"]'
            ) === button
          );

        const dynamicCheckoutClicked =
          Boolean(
            target.closest(
              DYNAMIC_PAYMENT_SELECTOR
            )
          );

        if (
          !addToCartClicked &&
          !dynamicCheckoutClicked
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        ensureMessage();
        applyRequiredButton();
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (
          allRequiredSelected() ||
          event.target !==
            mainForm()
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        ensureMessage();
        applyRequiredButton();
      },
      true
    );

    document.addEventListener(
      'comenii:instant-product-switch',
      (event) => {
        const product =
          event.detail?.product;

        if (!product) {
          return;
        }

        resetForProductSwitch(
          product
        );
      }
    );
  }

  function startInitialObserver() {
    initialObserver
      ?.disconnect();

    initialObserver =
      new MutationObserver(() => {
        if (
          userInteracted ||
          clearing ||
          initialFrame
        ) {
          return;
        }

        initialFrame =
          requestAnimationFrame(
            () => {
              initialFrame =
                0;

              clearInitialSelection();
            }
          );
      });

    initialObserver.observe(
      productScope(),
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'checked',
          'aria-checked',
          'class'
        ]
      }
    );

    window.setTimeout(
      () => {
        initialObserver
          ?.disconnect();

        initialObserver =
          null;
      },
      15000
    );
  }

  function startInitialClearProtection() {
    if (initialTimer) {
      window.clearInterval(
        initialTimer
      );
    }

    const startedAt =
      Date.now();

    initialTimer =
      window.setInterval(
        () => {
          if (userInteracted) {
            window.clearInterval(
              initialTimer
            );

            initialTimer =
              null;

            return;
          }

          if (
            Date.now() -
              startedAt >
            15000
          ) {
            window.clearInterval(
              initialTimer
            );

            initialTimer =
              null;

            return;
          }

          clearInitialSelection();
        },
        150
      );
  }

  function initialize() {
    /*
     * Remove any old duplicate message that
     * may have been created by the previous
     * temporary script.
     */
    document
      .querySelectorAll(
        '.comenii-size-red-message'
      )
      .forEach((element) => {
        element.remove();
      });

    installStyles();
    installListeners();

    clearInitialSelection();

    startInitialObserver();
    startInitialClearProtection();

    [
      0,
      30,
      80,
      150,
      300,
      600,
      1000,
      1500,
      2500,
      4000,
      6000,
      9000,
      12000
    ].forEach((delay) => {
      window.setTimeout(
        () => {
          if (!userInteracted) {
            clearInitialSelection();
          }
        },
        delay
      );
    });
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();